import React, { useState, useEffect, useCallback } from 'react';
import { fetchSubCountyAnalysis, fetchSubStreamDetails } from '../services/analysisService';
import AchievementBar from '../components/AchievementBar';
import { calculatePerformance, getHeatmapClass } from '../utils/analysisUtils';
import { Calendar, ListTree } from 'lucide-react';
import { stream } from 'xlsx';
import { use } from 'react';

const SubCountyCurrentTab = ({ subCountyId, fyStart, data, loading }) => {
  const [subStreamData, setSubStreamData] = useState({});
  const [expansion, setExpansion] = useState({}); // { stream: 'monthly' | 'substream' | null }

  /*
  useEffect(() => {
    fetchSubCountyAnalysis(fyStart, subCountyId).then(res => {
      const grouped = res.reduce((acc, row) => {
        if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
        acc[row.rev_stream].push(row);
        return acc;
      }, {});
      setData(grouped);
      setLoading(false);
    });
  }, [subCountyId, fyStart]);
*/
  // Reset expansion whenever the subCountyId or fyStart changes
useEffect(() => {
  setExpansion({});
  setSubStreamData({}); // Also clear cached sub-stream data to ensure fresh loads
}, [subCountyId, fyStart]);

  const handleSubStreamExpand = async (stream) => {
    //let details;
    // setSubStreamData(prev => ({ ...prev, [stream]: null })); // Clear previous data to show loading state
    if (expansion[stream] === 'substream') {
      setExpansion(prev => ({ ...prev, [stream]: null }));
      return;
    }
    if (!subStreamData[stream]) {
      const details = await fetchSubStreamDetails(subCountyId, stream, fyStart);
      setSubStreamData(prev => ({ ...prev, [stream]: details }));
    }
    setExpansion(prev => ({ ...prev, [stream]: 'substream' }));
  };

  // Calculate overall Sub-County totals
  const grandTotals = Object.values(data).flat().reduce((acc, curr) => {
    acc.actual += curr.amount_fy0 || 0;
    return acc;
  }, { actual: 0 });

  // Sum unique stream targets
  const totalTarget = Object.values(data).reduce((sum, months) => 
    sum + (months[0]?.annual_target || 0), 0);

  const totalPerf = calculatePerformance(grandTotals.actual, totalTarget);

  // Prepare Monthly Summary for Grand Total expansion
  // Add target calculation to the monthlyGrandTotals array in both components
const monthlyGrandTotals = Array.from({ length: 12 }, (_, i) => {
  const monthsInOrder = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
  const monthName = monthsInOrder[i];
  const targetKey = `${monthName.substring(0, 3).toLowerCase()}_target`;
  
  return Object.values(data).flat()
    .filter(item => item.calendar_month === monthName)
    .reduce((acc, curr) => {
      acc.month = monthName;
      acc.actual += curr.amount_fy0 || 0;
      acc.target += curr.monthly_target || 0; // Sum up targets for all streams
      // ... include fyMinus1/2 for ComparisonTab if needed
      return acc;
    }, { month: monthName, actual: 0, target: 0 });
});

  //handleSubStreamExpand(null); // Reset expansion when subCountyId or fyStart changes

  if (loading) return <div className="p-10 text-center">Loading current performance...</div>;

  return (
    <div className="bg-white rounded-xl border shadow-lg overflow-hidden">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 text-xs uppercase font-bold text-gray-600 border-b">
          <tr>
            <th className="px-4 py-4">Revenue Stream</th>
            <th className="px-4 py-4">Current FY Actual</th>
            <th className="px-4 py-4">Current FY Target</th>
            <th className="px-4 py-4">Achievement against Current Target</th>
            <th className="px-4 py-4">Expand</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([stream, months]) => {
            const actual = months.reduce((s, m) => s + m.amount_fy0, 0);
            const target = months[0]?.annual_target || 0;
            const perf = calculatePerformance(actual, target);
            const currentExp = expansion[stream];

            return (
              <React.Fragment key={stream}>
                <tr className="border-b hover:bg-gray-50">
                  <td className="px-4 py-4 font-bold">{stream}</td>
                  <td className="px-4 py-4 font-bold text-blue-700">{actual.toLocaleString()}</td>
                  <td className="px-4 py-4">{target.toLocaleString()}</td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getHeatmapClass(perf)}`}>{perf.toFixed(1)}%</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-center space-x-2">
                      <button onClick={() => setExpansion(p => ({...p, [stream]: currentExp === 'monthly' ? null : 'monthly'}))} className={`p-1.5 rounded ${currentExp === 'monthly' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}><Calendar size={18} /></button>
                      <button onClick={() => handleSubStreamExpand(stream)} className={`p-1.5 rounded ${currentExp === 'substream' ? 'bg-purple-100 text-purple-600' : 'text-gray-400'}`}><ListTree size={18} /></button>
                    </div>
                  </td>
                </tr>

                {/* Monthly Expansion */}
                {/* Inside SubCountyCurrentTab Monthly Expansion */}
                {currentExp === 'monthly' && months.map((m, i) => {
                  const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
                  const monthlyTarget = m.monthly_target || 0;
                  const monthlyPerf = monthlyTarget > 0 ? (m.amount_fy0 / monthlyTarget) * 100 : 0;

                  return (
                    <tr key={`m-${i}`} className="bg-blue-50/20 text-xs italic border-b">
                      <td className="px-12 py-2">{m.calendar_month}</td>
                      <td className="px-4 py-2 text-blue-600 font-semibold">{m.amount_fy0.toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-500">{monthlyTarget.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getHeatmapClass(monthlyPerf)}`}>
                          {monthlyPerf.toFixed(0)}%
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  );
                })}

                {/* Sub-stream Expansion */}
                {currentExp === 'substream' && (
                  <tr className="bg-purple-50/30 border-b">
                    <td colSpan={5} className="px-12 py-4">
                      <div className="grid grid-cols-2 gap-4 border-l-4 border-purple-400 pl-4">
                        {(subStreamData[stream] || []).map((ss, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border border-purple-100 shadow-sm">
                            <span className="text-xs text-gray-600">{ss.rev_sub_stream}</span>
                            <span className="text-xs font-bold text-purple-800">{Number(ss.amount).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {/* ... existing tbody ... */}
        <tfoot className="border-t-4 border-blue-200">
          <tr className="bg-blue-50 font-extrabold">
            <td className="px-6 py-5 text-blue-900">GRAND TOTAL (SUB-COUNTY)</td>
            <td className="px-4 py-5 text-blue-900 text-lg">
              {grandTotals.actual.toLocaleString()}
              <AchievementBar actual={grandTotals.actual} target={totalTarget} />
            </td>
            <td className="px-4 py-5 text-gray-700">{totalTarget.toLocaleString()}</td>
            <td className="px-4 py-5 text-center">
              <span className={`px-4 py-1.5 rounded-full text-sm font-black shadow-sm ${getHeatmapClass(totalPerf)}`}>
                {totalPerf.toFixed(1)}%
              </span>
            </td>
            <td className="px-4 py-5 text-center">
              <button 
                onClick={() => setExpansion(p => ({...p, grandTotal: p.grandTotal === 'monthly' ? null : 'monthly'}))}
                className={`p-2 rounded-lg transition-all ${expansion.grandTotal === 'monthly' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-blue-600 border border-blue-200'}`}
              >
                <Calendar size={20} />
              </button>
            </td>
          </tr>

          {/* Grand Total Monthly Expansion */}
          {expansion.grandTotal === 'monthly' && monthlyGrandTotals.map((m, idx) => (
            <tr key={`total-m-${idx}`} className="bg-blue-50/40 text-xs font-bold italic border-b text-blue-800">
              <td className="px-12 py-3 border-r">{m.month} (All Streams)</td>
              <td className="px-4 py-3 text-blue-700">{m.actual.toLocaleString()}</td>
              <td className="px-4 py-3 text-gray-500">{m.target.toLocaleString()}</td>
              <td className="px-4 py-3 text-center">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getHeatmapClass(calculatePerformance(m.actual, m.target))}`}>
                  {calculatePerformance(m.actual, m.target).toFixed(0)}%
                </span>
              </td>
              <td colSpan={3}></td>
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
};

export default SubCountyCurrentTab;